import { Box, Center, Divider, Flex, HStack, Icon, Image, Text, Textarea } from '@chakra-ui/react';
import { useState } from 'react';
import { FaExclamationTriangle } from 'react-icons/fa';

import { Checkmark } from '../../../../../assets/Images';
import ButtonedRadioGroup from '../../../../../components/ButtonedRadioGroup';
import Button from '../../../../../components/ui/buttons/Button';
import { PostLeadByExternalIdDelistMutationRequest } from '../../../../../services/marketplace/generated/kubb/models/PostLeadByExternalIdDelist';

export type DelistReason = PostLeadByExternalIdDelistMutationRequest['reason'];

export const NOTE_MAX_LENGTH = 140;

const DELIST_REASON_OPTIONS = (
  [
    { value: 'keep_car_buyout', label: 'I want to keep the car / buy out my lease instead' },
    {
      value: 'new_vehicle_at_dealer',
      label: "I'm turning it in and leasing or buying something new at a dealer",
    },
    { value: 'sold_elsewhere', label: 'I sold it somewhere else' },
    { value: 'offers_too_low', label: 'The offers were too low' },
    { value: 'not_ready_yet', label: "I'm not ready to sell yet, maybe later" },
    { value: 'other', label: 'Other' },
  ] as { value: DelistReason; label: string }[]
).map((option) => ({
  ...option,
  selectedImage: (
    <Image
      zIndex={4}
      boxSize="28px"
      src={Checkmark}
      alt="Checkmark"
      position="absolute"
      left={-4}
      top="50%"
      transform="translateY(-50%)"
    />
  ),
}));

type DelistConfirmStepProps = {
  activeOfferCount: number;
  errorMessage: string | null;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: (reason: DelistReason, note: string) => void;
};

const DelistConfirmStep = ({
  activeOfferCount,
  errorMessage,
  isPending,
  onCancel,
  onConfirm,
}: DelistConfirmStepProps) => {
  const [reason, setReason] = useState<DelistReason | null>(null);
  const [note, setNote] = useState('');

  const consequences = [
    'Your vehicle will no longer be visible to buyers.',
    activeOfferCount > 0
      ? `All ${activeOfferCount} of your current ${
          activeOfferCount === 1 ? 'offer' : 'offers'
        } will be forfeited and cannot be recovered.`
      : 'Any offers already sent will be forfeited and cannot be recovered.',
    "Relisting isn't self-serve, you'll need to call support to list again.",
  ];

  return (
    <Box
      px={{ base: '24px', md: '32px' }}
      pb={{ base: '24px', md: '32px' }}
      pt={{ base: '24px', md: '28px' }}
    >
      <HStack
        spacing="6px"
        bg="leaseEndRedBG"
        color="leaseEndRed"
        borderRadius="full"
        px="10px"
        py="4px"
        w="fit-content"
      >
        <Icon as={FaExclamationTriangle} boxSize="10px" />
        <Text fontSize="11px" fontWeight="700" letterSpacing="0.06em" lineHeight="1">
          CONFIRM DELISTING
        </Text>
      </HStack>

      <Text fontFamily="heading" fontSize="22px" fontWeight="700" color="leaseEndBlue" mt="12px">
        Remove your listing from the marketplace?
      </Text>

      {activeOfferCount > 0 && (
        <Flex
          gap="10px"
          bg="ivoryGlow"
          borderWidth="1px"
          borderColor="leaseEndYellow"
          borderRadius="10px"
          p="12px"
          mt="16px"
        >
          <Center
            w="22px"
            h="22px"
            borderRadius="full"
            bg="leaseEndYellow"
            color="leaseEndBlue"
            fontSize="12px"
            fontWeight="800"
            flexShrink={0}
          >
            {activeOfferCount}
          </Center>
          <Box>
            <Text fontSize="13.5px" fontWeight="700" color="leaseEndBlue" lineHeight="1.45">
              You currently have {activeOfferCount} {activeOfferCount === 1 ? 'offer' : 'offers'},
              which will be withdrawn and cannot be restored.
            </Text>
            <Text fontSize="12.5px" color="gray.600" mt="2px">
              Buyers will be notified your listing is no longer available.
            </Text>
          </Box>
        </Flex>
      )}

      <Divider my="16px" borderColor="grayMediumBackground" />

      <Text fontSize="13px" color="gray.500" mb="12px">
        Here's what happens if you continue:
      </Text>
      <Flex direction="column" gap="12px" mb="20px">
        {consequences.map((consequence, index) => (
          <Flex key={consequence} gap="10px" align="flex-start">
            <Center
              w="18px"
              h="18px"
              borderRadius="full"
              bg="leaseEndRedBG"
              color="leaseEndRed"
              fontSize="11px"
              fontWeight="700"
              flexShrink={0}
              mt="1px"
            >
              {index + 1}
            </Center>
            <Text fontSize="14px" color="gray.700" lineHeight="1.45">
              {consequence}
            </Text>
          </Flex>
        ))}
      </Flex>

      <Text fontSize="14px" fontWeight="700" color="leaseEndBlue" mb="8px">
        Help us understand: why are you delisting?
      </Text>
      <ButtonedRadioGroup
        name="delist-reason"
        value={reason ?? undefined}
        onChange={(value) => setReason(value as DelistReason)}
        options={DELIST_REASON_OPTIONS}
        spacing={2}
        collapseOnMobile={false}
        itemProps={{ py: 3, px: 4, minH: 'auto', borderRadius: 'xl', shadow: 'none' }}
        itemTextProps={{ fontSize: '13.5px', fontWeight: '600', lineHeight: '1.35' }}
      />

      {reason === 'other' && (
        <Box mt="16px">
          <Textarea
            aria-label="Tell us more"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Tell us more (optional)"
            maxLength={NOTE_MAX_LENGTH}
            rows={3}
            resize="none"
            fontSize="14px"
            borderRadius="12px"
            borderColor="gainsboro"
            _placeholder={{ color: 'gray.500' }}
            _focusVisible={{ borderColor: 'leaseEndBlue', boxShadow: 'none' }}
          />
          <Text fontSize="11px" color="gray.500" textAlign="right" mt="4px">
            {note.length}/{NOTE_MAX_LENGTH}
          </Text>
        </Box>
      )}

      <Divider mt="24px" mb="16px" borderColor="grayMediumBackground" />
      {errorMessage != null && (
        <Text fontSize="13px" color="leaseEndRed" fontWeight="600" mb="12px">
          {errorMessage}
        </Text>
      )}
      <Flex gap="10px">
        <Button
          flex="1"
          variant="secondary"
          onClick={onCancel}
          isDisabled={isPending}
          trackClick="marketplace_delist_keep_listing"
          h="auto"
          py="14px"
        >
          Keep listing active
        </Button>
        <Button
          flex="1"
          variant="danger"
          onClick={() => reason != null && onConfirm(reason, note)}
          isDisabled={reason == null}
          isLoading={isPending}
          trackClick="marketplace_delist_confirm"
          letterSpacing="1px"
          textTransform="uppercase"
          h="auto"
          py="14px"
        >
          Delist my vehicle
        </Button>
      </Flex>
    </Box>
  );
};

export default DelistConfirmStep;
